import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";

import { PageHeader } from "@/components/ui/page-header";
import { ChartSettingsProvider } from "@/components/portal/charts/settings/ChartSettingsProvider";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { usePermissions } from "@/hooks/usePermissions";
import { canAuthorInsightReports } from "@/lib/insightReportsAccess";
import type { InsightReport } from "@/services/api/insightReports";

import { InsightReportBuilder } from "./InsightReportBuilder";

export const Route = createFileRoute("/portal/knowledgebase-sql/insight-reports/new")({
  component: NewInsightReport,
});

function NewInsightReport() {
  const navigate = useNavigate();
  const { selectedOrganisation } = usePortalData();
  const { isOwner, isAdmin, isPlatformAdmin } = usePermissions();
  const canAuthor = canAuthorInsightReports({ isOwner, isAdmin, isPlatformAdmin });
  const [report, setReport] = useState<InsightReport | null>(null);

  if (!canAuthor) {
    return (
      <div className="p-6">
        <p className="text-sm text-muted-foreground">
          You don't have access to build Insight Reports.
        </p>
      </div>
    );
  }

  return (
    // #1024 item 2: same provider `$slug.tsx`'s page mounts, so this
    // builder's live preview picks up the organisation's palette too,
    // instead of the L4-only default every chart falls back to with no
    // provider ancestor.
    <ChartSettingsProvider organisationId={selectedOrganisation?.organisationId}>
      <div className="space-y-6 p-6">
        <Helmet>
          <title>New Insight Report</title>
        </Helmet>
        <PageHeader
          title="New Insight Report"
          description="Ask a question about your data — the answer becomes the report's first section."
        />
        <InsightReportBuilder
          report={report}
          onReportChanged={(updated) => {
            setReport(updated);
            if (!report) {
              // First section just saved — the report now exists; move to its
              // own page so the URL, refresh-on-open, and further section adds
              // all go through the same slug-addressed route as any other view.
              void navigate({
                to: "/portal/knowledgebase-sql/insight-reports/$slug",
                params: { slug: updated.slug },
              });
            }
          }}
        />
      </div>
    </ChartSettingsProvider>
  );
}
